import { useEffect, useState } from "react";

import { adminApi } from "../../api/admin";

interface Props {
  value: string;
  scopeType: string;
  validTokens: string[];
  onChange: (next: string) => void;
  onValidityChange?: (valid: boolean) => void;
}

export function BriefingInstructionField({
  value,
  scopeType,
  validTokens,
  onChange,
  onValidityChange,
}: Props) {
  const [tokenError, setTokenError] = useState<string | null>(null);
  const validateTokens = adminApi.cronJobs.validateTokens();

  // Live token validation (server-side)
  useEffect(() => {
    if (!value || !scopeType) {
      setTokenError(null);
      return;
    }
    const handle = window.setTimeout(() => {
      validateTokens.mutate(
        { template: value, scope_type: scopeType },
        {
          onSuccess: (res) => {
            // Malformed first: an undoubled literal brace kills the cron
            // silently on every fire, so it outranks a scope mismatch.
            if (res.malformed_fields?.length > 0) {
              setTokenError(
                `Literal braces the renderer would read as tokens: ${res.malformed_fields.join(", ")}. Double them ({{ and }}) — a JSON example in a briefing is a replacement field unless escaped.`,
              );
            } else if (res.unknown_tokens.length > 0) {
              setTokenError(
                `Unknown tokens for scope '${scopeType}': ${res.unknown_tokens.join(", ")}. Valid: ${res.valid_tokens.join(", ")}.`,
              );
            } else {
              setTokenError(null);
            }
          },
          onError: () => setTokenError(null),
        },
      );
    }, 300);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, scopeType]);

  useEffect(() => {
    onValidityChange?.(!tokenError);
  }, [tokenError, onValidityChange]);

  return (
    <div>
      <label className="label">Briefing instruction</label>
      <textarea
        className="input"
        rows={8}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Describe what Jarvis should do when the cron fires…"
      />
      <div className="mt-1 text-xs text-ink-low">
        Available tokens for scope <code>{scopeType}</code>:{" "}
        {["date", "weekday", "week_id", "last_run", ...validTokens]
          .map((t) => `{${t}}`)
          .join(" · ")}
      </div>
      {tokenError ? (
        <p className="mt-1 text-sm text-danger">{tokenError}</p>
      ) : null}
    </div>
  );
}
